import { apiFetch } from "@/auth/apiClient";

// ── Types ──────────────────────────────────────────────────────────────────────

export type StudentProfileResponse = {
  user: {
    id: number;
    email: string;
    role: string;
    userType: string;
    createdAt: string;
    isVerified: boolean;
  };
  profile: {
    firstName: string;
    lastName?: string | null;
    contactNumber?: string | null;
    image?: string | null;
  } | null;
  stats: {
    totalCoursesPurchased: number;
    totalCoursesCompleted: number;
    totalMockTestsPurchased: number;
    totalMockTestsAttempted: number;
    averageMockTestScore: number | null;
    lastActivity: string | null;
  };
};

export type ChapterExamInfo = {
  examId: string;
  totalMarks: number;
  passMarks: number;
  maxAttempts: number;
  attemptsUsed: number;
  bestScore: number | null;
  latestScore: number | null;
  isPassed: boolean;
  lastAttemptDate: string | null;
  attempts: Array<{
    id: string;
    attemptNumber: number;
    score: number;
    isPassed: boolean;
    startedAt: string;
    submittedAt: string | null;
    answers: Record<string, unknown> | null;
  }>;
};

export type ChapterProgressDetail = {
  id: string;
  title: string;
  order: number;
  totalLessons: number;
  completedLessons: number;
  remainingLessons: number;
  exam: ChapterExamInfo | null;
};

export type CourseProgressItem = {
  enrollmentId: string;
  enrolledAt: string;
  course: {
    id: string;
    title: string;
    level: string | null;
    levels: string[] | null;
    type: string;
    thumbnail: string | null;
  };
  progress: {
    percentage: number;
    completedLessons: number;
    currentChapterId: string | null;
    lastAccessedAt: string | null;
    isCompleted: boolean;
  };
  chapters: ChapterProgressDetail[];
};

export type MockTestAttemptDetail = {
  id: string;
  attemptNumber: number;
  score: number;
  totalMarks: number;
  passingMarks: number;
  isPassed: boolean;
  startedAt: string;
  submittedAt: string | null;
  durationMs: number | null;
  answers: Record<string, unknown> | null;
};

export type CategoryBreakdown = {
  name: string;
  earned: number;
  total: number;
  percentage: number;
  passMarks: number | null;
  isPassed: boolean | null;
};

export type MockTestHistoryItem = {
  purchase: {
    id: string;
    purchasedAt: string;
    purchasedAttempts: number;
    remainingAttempts: number;
    amountPaid: number;
    expiresAt: string | null;
  } | null;
  mockTest: {
    id: string;
    title: string;
    level: string;
    examType: string;
    duration: number;
    totalMarks: number;
    passingMarks: number;
    categoryPassMarks: Record<string, number> | null;
  };
  stats: {
    attemptsUsed: number;
    bestScore: number | null;
    latestScore: number | null;
    latestIsPassed: boolean;
    averageScore: number | null;
    passedAttempts: number;
    failedAttempts: number;
  };
  attempts: MockTestAttemptDetail[];
  latestCategoryBreakdown: CategoryBreakdown[] | null;
};

export type StudentNote = {
  id: string;
  targetUserId: number;
  authorId: number;
  authorEmail: string;
  content: string;
  createdAt: string;
  updatedAt: string;
};

// ── API Functions ──────────────────────────────────────────────────────────────

export async function getStudentProfile(userId: number): Promise<StudentProfileResponse> {
  return apiFetch<StudentProfileResponse>(`/admin/students/${userId}/profile`, { method: "GET" });
}

export async function getStudentCourseProgress(userId: number): Promise<CourseProgressItem[]> {
  return apiFetch<CourseProgressItem[]>(`/admin/students/${userId}/course-progress`, { method: "GET" });
}

export async function getStudentMockTestHistory(userId: number): Promise<MockTestHistoryItem[]> {
  return apiFetch<MockTestHistoryItem[]>(`/admin/students/${userId}/mock-test-history`, { method: "GET" });
}

export async function getStudentNotes(userId: number): Promise<StudentNote[]> {
  return apiFetch<StudentNote[]>(`/admin/students/${userId}/notes`, { method: "GET" });
}

export async function createStudentNote(userId: number, content: string): Promise<StudentNote> {
  return apiFetch<StudentNote>(`/admin/students/${userId}/notes`, { method: "POST", json: { content } });
}

export async function deleteStudentNote(userId: number, noteId: string): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(`/admin/students/${userId}/notes/${noteId}`, { method: "DELETE" });
}
